/**
 * Convierte el contenido JSON de una detección en una diapositiva del carrusel de imágenes.
 * Los tipos con imagen guardan el archivo en files.image[] (o files.sequence[] si son varios
 * frames de una secuencia corta) y las detecciones en un arreglo.
 *
 * Anclaje de caja: center-middle como forensic-reconstruction-ui (DetectionCanvas.tsx), salvo
 * reconocimiento facial, que usa frame.x/y como esquina superior izquierda (mismo criterio).
 * Colores: paleta pastel del BI (theme CAROUSEL_DETECTION_COLORS), alineada a series de
 * gráficos, no al magenta fuerte del visor forense.
 */

import { CAROUSEL_DETECTION_COLORS } from '../theme.js';

export const IMAGE_CAROUSEL_DETECTION_KEYS = {
  LineCrossingDetection: 'detections',
  ObjectRecognition: 'objects',
  StoppedObjectsDetector: 'objects',
  PoseDetection: 'poses',
  FaceRecognition: 'faces',
  MultiZoneObjectTracking: 'detections',
};

/** Tipos cuyo frame.x/y es la esquina superior izquierda, no el centro (igual que DetectionCanvas.tsx). */
const LEFT_TOP_ANCHOR_TYPES = new Set(['FaceRecognition']);

const DETECTION_COLORS = {
  person: CAROUSEL_DETECTION_COLORS.person,
  car: CAROUSEL_DETECTION_COLORS.car,
};
const DEFAULT_DETECTION_COLOR = CAROUSEL_DETECTION_COLORS.default;
const HIGHLIGHT_COLOR = CAROUSEL_DETECTION_COLORS.highlight;
const SUCCESS_COLOR = CAROUSEL_DETECTION_COLORS.success;

export function imageCarouselSupportsSensorType(sensorType) {
  return Object.prototype.hasOwnProperty.call(IMAGE_CAROUSEL_DETECTION_KEYS, sensorType);
}

/** Ruta relativa del archivo en VSaaS. El token lo agrega el servidor, no se guarda aqui. */
export function buildVsaasImagePath(sensorId, file) {
  if (!sensorId || !file?.container || !file?.name) return '';
  const path = `sensors/${encodeURIComponent(sensorId)}/datasets/${encodeURIComponent(file.container)}/download/${encodeURIComponent(file.name)}`;
  return file.datasourceName ? `${path}?datasourceName=${encodeURIComponent(file.datasourceName)}` : path;
}

function parseContent(content) {
  if (typeof content !== 'string') return content;
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
}

/**
 * Primera imagen utilizable: files.image[] (la mayoría de los tipos) o, si no hay, la primera
 * de files.sequence[] (seguimiento multizona, que guarda varios frames de una secuencia corta
 * en vez de una sola imagen).
 */
function firstImageFile(content) {
  const images = content?.files?.image;
  if (Array.isArray(images)) {
    const found = images.find((file) => file && typeof file === 'object' && file.name);
    if (found) return found;
  }
  const sequence = content?.files?.sequence;
  if (Array.isArray(sequence)) {
    return sequence.find((file) => file && typeof file === 'object' && file.name) || null;
  }
  return null;
}

function toFrame(frame) {
  if (!frame || typeof frame !== 'object') return null;
  const x = Number(frame.x);
  const y = Number(frame.y);
  const w = Number(frame.w);
  const h = Number(frame.h);
  if (![x, y, w, h].every(Number.isFinite)) return null;
  return { x, y, w, h };
}

/**
 * Caja desde frame.x/y: centrada ("center-middle", default) o con x/y como esquina superior
 * izquierda ("left-top", caras), igual que getBoundingBox del visor forense.
 */
export function boundingBoxFromFrame(frame, mode = 'center-middle') {
  const f = toFrame(frame);
  if (!f) return null;
  if (mode === 'left-top') {
    return { left: f.x, top: f.y, width: f.w, height: f.h };
  }
  return { left: f.x - f.w / 2, top: f.y - f.h / 2, width: f.w, height: f.h };
}

/**
 * Color de borde por clase; ciruela pastel para cruce de línea / objeto detenido.
 * Cuando alguna detección del mismo cuadro está marcada (needsHighlight), el resto se pinta
 * en el color de "resto del cuadro", igual que el semáforo rojo/verde del visor forense.
 */
export function detectionColor(detection, needsHighlight = false) {
  if (!detection) return DEFAULT_DETECTION_COLOR;
  if (detection.stopped === true || detection.direction || detection.triggerSubject === true) {
    return HIGHLIGHT_COLOR;
  }
  if (needsHighlight) return SUCCESS_COLOR;
  return DETECTION_COLORS[detection.class] || DEFAULT_DETECTION_COLOR;
}

function toDetection(detection, needsHighlight, sensorType) {
  if (!detection || typeof detection !== 'object') return null;
  const probability = Number(detection.probability);
  const anchorMode = LEFT_TOP_ANCHOR_TYPES.has(sensorType) ? 'left-top' : 'center-middle';
  return {
    class: detection.class ?? '',
    // Nombre del sujeto identificado (reconocimiento facial); no hay "class" en ese caso.
    subject: detection.subject ?? null,
    // Texto leído (patente); class sigue siendo "license_plate", no sirve como etiqueta.
    value: detection.value != null ? String(detection.value) : null,
    probability: Number.isFinite(probability) ? probability : null,
    frame: toFrame(detection.frame),
    box: boundingBoxFromFrame(detection.frame, anchorMode),
    color: detectionColor(detection, needsHighlight),
    frameId: detection.frameId ?? null,
    direction: detection.direction ?? null,
    stopped: typeof detection.stopped === 'boolean' ? detection.stopped : null,
    triggerSubject: typeof detection.triggerSubject === 'boolean' ? detection.triggerSubject : null,
  };
}

/**
 * Centroide de una detección para el filtro de zona: en los pies para personas, igual que el
 * visor forense (getDetectionCentroid, detectionUtils.ts), y en frame.x/y para el resto.
 */
export function detectionCentroid(detection) {
  const frame = detection?.frame;
  if (!frame) return null;
  if (detection.class === 'person') {
    return { x: frame.x, y: frame.y + frame.h / 2 };
  }
  return { x: frame.x, y: frame.y };
}

/**
 * Punto dentro de uno o varios polígonos (ray casting, mismo algoritmo que isInsideZone del
 * visor forense). Sin zona configurada no hay restricción: se considera dentro, para que un
 * sensor sin polígono no quede excluido por el filtro "mostrar solo dentro de zona".
 *
 * Un "polígono" de menos de 3 puntos no delimita un área (es una línea, como la que usan los
 * tipos de cruce de línea), así que tampoco restringe: mismo catálogo de tipos de sensor de
 * VSaaS, que exige mínimo 3 puntos para una zona real y como máximo 2 para una línea.
 */
export function isInsideZone(point, zones) {
  if (!point) return true;
  const realZones = Array.isArray(zones) ? zones.filter((z) => Array.isArray(z) && z.length >= 3) : [];
  if (realZones.length === 0) return true;
  for (const zone of realZones) {
    let inside = false;
    for (let i = 0, j = zone.length - 1; i < zone.length; j = i++) {
      const xi = zone[i].x;
      const yi = zone[i].y;
      const xj = zone[j].x;
      const yj = zone[j].y;
      const intersect = yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }
    if (inside) return true;
  }
  return false;
}

/**
 * Una diapositiva está dentro de la zona si alguna de sus detecciones lo está (o si no tiene
 * detecciones con frame, caso en el que no hay nada que evaluar y no se excluye).
 */
export function slideIsInsideZone(slide, zones) {
  const detections = slide?.detections || [];
  if (!detections.length) return true;
  return detections.some((d) => isInsideZone(detectionCentroid(d), zones));
}

/**
 * Devuelve la diapositiva de una fila o null si el tipo no tiene imagen o la fila no trae archivo.
 * content puede venir como objeto o como texto JSON.
 */
export function mapContentToSlide(content, { sensorType, sensorId } = {}) {
  if (!imageCarouselSupportsSensorType(sensorType)) return null;
  const parsed = parseContent(content);
  if (!parsed || typeof parsed !== 'object') return null;
  const file = firstImageFile(parsed);
  if (!file) return null;
  const list = Array.isArray(parsed[IMAGE_CAROUSEL_DETECTION_KEYS[sensorType]])
    ? parsed[IMAGE_CAROUSEL_DETECTION_KEYS[sensorType]]
    : [];
  // Con varios frames candidatos (files.sequence), cada detección indica el suyo con frameId;
  // solo se muestran las del frame elegido para no desalinear las cajas sobre la imagen.
  const frameKey = file.originalFilename || file.name;
  const hasFrameIds = list.some((d) => d && d.frameId != null);
  const sceneList = hasFrameIds ? list.filter((d) => d && d.frameId === frameKey) : list;
  const needsHighlight = sceneList.some(
    (d) => d && (d.stopped === true || !!d.direction || d.triggerSubject === true)
  );
  return {
    sensorType,
    sensorId: sensorId ?? null,
    imagePath: buildVsaasImagePath(sensorId, file),
    imageName: file.originalFilename || file.name,
    // Piezas del archivo, para pedirle la imagen al servidor sin volver a parsear imagePath.
    imageFile: {
      container: file.container,
      name: file.name,
      datasourceName: file.datasourceName || null,
    },
    detections: sceneList.map((d) => toDetection(d, needsHighlight, sensorType)).filter(Boolean),
  };
}

/**
 * Deja en cada diapositiva solo las detecciones cuya class coincide (sin distinguir mayúsculas),
 * y descarta las diapositivas que se quedan sin ninguna. Para un sensor_type como
 * ObjectRecognition, que mezcla varias clases en la misma escena (persona, auto, patente, etc.),
 * esto permite armar un carrusel de "solo una clase" sin tocar la query SQL. Sin classFilter,
 * devuelve las diapositivas tal cual llegaron.
 */
export function filterSlidesByClass(slides, classFilter) {
  const list = Array.isArray(slides) ? slides : [];
  const normalized = String(classFilter || '').trim().toLowerCase();
  if (!normalized) return list;
  return list
    .filter(Boolean)
    .map((slide) => ({
      ...slide,
      detections: (slide.detections || []).filter((d) => String(d?.class || '').toLowerCase() === normalized),
    }))
    .filter((slide) => slide.detections.length > 0);
}
